import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import type { ApiTokenSummary } from '../../api/types';
import { useApiTokens, useCreateToken, useRevokeToken } from '../../api/settings-queries';
import { useSession } from '../../app/session-context';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Banner } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { CopyButton } from '../../ui/CopyButton';
import { InputField } from '../../ui/Input';
import { Stack, Row } from '../../ui/Stack';
import { StatusState } from '../../ui/StatusState';
import { Table, type TableColumn } from '../../ui/Table';

function formatDate(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

/** API tokens, in both modes. The plaintext of a new token lives only in this
 *  component's state: it is never refetched, so a remount loses it. */
export function TokenSettings(): ReactElement {
  const { snapshot } = useSession();
  const workspaceId = snapshot.workspaceId ?? '';
  const mcpEndpoint = snapshot.config?.mcpEndpoint ?? null;
  const tokens = useApiTokens(workspaceId, true);
  const create = useCreateToken(workspaceId);
  const revoke = useRevokeToken(workspaceId);
  const [minted, setMinted] = useState<{ name: string; token: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (name.length === 0) return;
    create.mutate(name, {
      onSuccess: (result) => {
        setMinted({ name: result.name, token: result.token });
        setCreating(false);
        setNewName('');
        create.reset();
      }
    });
  };

  const columns: TableColumn<ApiTokenSummary>[] = [
    { id: 'name', label: 'Name', isRowHeader: true, render: (t) => t.name },
    { id: 'created', label: 'Created', render: (t) => formatDate(t.createdAt) },
    { id: 'lastUsed', label: 'Last used', render: (t) => formatDate(t.lastUsedAt) },
    {
      id: 'actions',
      label: 'Actions',
      render: (t) => (
        <Button size="sm" variant="quiet" aria-label={`Revoke ${t.name}`} isDisabled={revoke.isPending} onPress={() => revoke.mutate(t.id)}>
          Revoke
        </Button>
      )
    }
  ];

  return (
    <Card title="API tokens">
      <Stack gap={4}>
        {minted ? (
          <Banner tone="success" title="Copy this token now — it won’t be shown again.">
            <Stack gap={2}>
              <Row gap={2}>
                <code>{minted.token}</code>
                <CopyButton value={minted.token} label="Copy" />
              </Row>
              {mcpEndpoint ? (
                <p className="m-0">
                  MCP endpoint: <code>{mcpEndpoint}</code>
                </p>
              ) : null}
            </Stack>
          </Banner>
        ) : null}

        {tokens.data && tokens.data.length > 0 ? (
          <Table label="API tokens" columns={columns} rows={tokens.data} />
        ) : tokens.data ? (
          <StatusState kind="empty" title="No tokens yet." />
        ) : null}

        <ErrorBanner error={tokens.error ?? create.error ?? revoke.error} />

        {creating ? (
          <form onSubmit={submit}>
            <Row gap={2}>
              <InputField label="Token name" value={newName} onChange={setNewName} />
              <Button type="submit" variant="primary" busy={create.isPending}>
                Create
              </Button>
              <Button
                variant="quiet"
                onPress={() => {
                  setCreating(false);
                  create.reset();
                }}
              >
                Cancel
              </Button>
            </Row>
          </form>
        ) : (
          <div>
            <Button
              onPress={() => {
                setMinted(null);
                setCreating(true);
              }}
            >
              Create token
            </Button>
          </div>
        )}
      </Stack>
    </Card>
  );
}
